/**
 * Primary Scope card.
 *
 * The configurator's anchor section. The buyer types the domain
 * they want assessed; the card runs a public discovery scan and
 * hands off to <SubdomainGrid /> for cherry-picking.
 *
 * State lives in the parent reducer; this component is a controlled
 * input bound to `state.primaryDomain` and a button bound to the
 * `onScan` callback that the parent threads from its
 * `runPrimaryDiscovery` effect.
 */

import { useCallback } from "react";

import type {
  ReconConfiguratorAction,
  ReconConfiguratorState,
} from "../state";

import "./primary-scope-card.css";

interface Props {
  state: ReconConfiguratorState;
  dispatch: React.Dispatch<ReconConfiguratorAction>;
  onScan: () => void | Promise<void>;
}

export function PrimaryScopeCard({ state, dispatch, onScan }: Props) {
  const trimmed = state.primaryDomain.trim();
  const isLoading = state.primaryDiscovery.kind === "loading";
  const isError = state.primaryDiscovery.kind === "error";
  const isReady = state.primaryDiscovery.kind === "ok";

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!trimmed || isLoading) return;
      void onScan();
    },
    [trimmed, isLoading, onScan],
  );

  return (
    <section
      className="primary-scope-card"
      aria-labelledby="primary-scope-card-heading"
    >
      <div className="primary-scope-card-eyebrow">STEP 1 · PRIMARY DOMAIN</div>
      <h2 id="primary-scope-card-heading">Your apex domain</h2>
      <p className="primary-scope-card-body">
        Enter the domain your business is known by. We'll fingerprint
        its DNS posture, SSL/TLS configuration, email authentication,
        web tech stack, and surface every related subdomain we can
        find from certificate-transparency logs and passive DNS — so
        you can pick what's in scope.
      </p>

      <form className="primary-scope-card-form" onSubmit={handleSubmit}>
        <label htmlFor="primary-domain-input" className="visually-hidden">
          Primary domain
        </label>
        <input
          id="primary-domain-input"
          type="text"
          inputMode="url"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          value={state.primaryDomain}
          onChange={(e) =>
            dispatch({ type: "set_primary_domain", value: e.target.value })
          }
          placeholder="acme.com"
          aria-invalid={isError || undefined}
          aria-describedby={
            isError ? "primary-scope-card-error" : undefined
          }
        />
        <button
          type="submit"
          className="primary-scope-card-scan-btn"
          disabled={!trimmed || isLoading}
        >
          {isLoading ? (
            <>
              <span
                className="primary-scope-card-spinner"
                aria-hidden="true"
              />
              <span>Scanning…</span>
            </>
          ) : isReady ? (
            <span>Rescan</span>
          ) : (
            <span>Scan</span>
          )}
        </button>
      </form>

      <p className="primary-scope-card-hint">
        Scanning is optional — it helps you find subdomains you might
        forget to include. If your apex alone is what you want
        assessed, leave the scan unstarted and continue below; we'll
        still investigate the apex top-to-bottom.
      </p>

      {isError && (
        <p
          id="primary-scope-card-error"
          className="primary-scope-card-status primary-scope-card-status-error"
          role="alert"
        >
          {state.primaryDiscovery.kind === "error"
            ? state.primaryDiscovery.message
            : ""}
        </p>
      )}

      {isReady && (
        <p
          className="primary-scope-card-status primary-scope-card-status-ok"
          role="status"
        >
          Discovered{" "}
          <strong>
            {state.primaryDiscovery.kind === "ok"
              ? state.primaryDiscovery.data.length
              : 0}
          </strong>{" "}
          hostname
          {state.primaryDiscovery.kind === "ok" &&
          state.primaryDiscovery.data.length === 1
            ? ""
            : "s"}
          . Pick which ones go into the scan below.
        </p>
      )}
    </section>
  );
}
